import {
	Button,
	Input,
	InputLabel,
	InputWrapper,
	Select,
	SelectTrigger,
	SelectContent,
	SelectItem,
	SelectValue,
} from '@/components/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';

interface CompanyFormProps {
	formTitle: string;
	onBack: () => void;
	onSubmit: (data: any) => void;
	isSubmitting: boolean;
}

export const CompanyForm = ({
	formTitle,
	onBack,
	onSubmit,
	isSubmitting,
}: CompanyFormProps) => {
	const [plan, setPlan] = useState('standard');
	const [active, setActive] = useState('true');
	const [paymentStatus, setPaymentStatus] = useState('PENDING');

	// Lógica de submissão (simplificada para focar na estrutura visual pedida)
	const handleInternalSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		const data = Object.fromEntries(formData.entries());
		onSubmit({
			...data,
			plan,
			active,
			payment_status: paymentStatus,
		});
	};

	return (
		<div className="animate-fade-in space-y-6">
			{/* Header do Formulário */}
			<div className="flex items-center gap-4">
				<button
					onClick={onBack}
					className="group flex h-10 w-10 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200"
				>
					<ArrowLeft
						size={18}
						className="text-stone-400 group-hover:text-stone-900"
					/>
				</button>
				<h2 className="text-2xl font-bold text-stone-900">
					{formTitle}
				</h2>
			</div>

			{/* Card do Formulário */}
			<form
				onSubmit={handleInternalSubmit}
				className="space-y-8 rounded-2xl border border-stone-100 bg-white p-8 shadow-sm"
			>
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					{/* Nome da Empresa - Full Width */}
					<div className="space-y-2 md:col-span-2">
						<InputLabel
							text="NOME DA EMPRESA"
							className="text-[10px] font-black uppercase tracking-widest text-stone-300"
						/>
						<InputWrapper>
							<Input
								name="name"
								placeholder="Ex: Acme Corp"
								required
							/>
						</InputWrapper>
					</div>

					{/* Plano */}
					<div className="space-y-2">
						<InputLabel
							text="PLANO"
							className="text-[10px] font-black uppercase tracking-widest text-stone-300"
						/>
						<InputWrapper>
							<Select
								value={plan}
								onValueChange={(value) => setPlan(value)}
							>
								<SelectTrigger className="flex-1 bg-transparent text-sm text-stone-900">
									<SelectValue>
										{plan === 'standard'
											? 'Standard Business'
											: 'Premium Enterprise'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="standard">
										Standard Business
									</SelectItem>
									<SelectItem value="premium">
										Premium Enterprise
									</SelectItem>
								</SelectContent>
							</Select>
						</InputWrapper>
					</div>

					{/* Valor Mensal */}
					<div className="space-y-2">
						<InputLabel
							text="VALOR MENSAL (R$)"
							className="text-[10px] font-black uppercase tracking-widest text-stone-300"
						/>
						<InputWrapper className="bg-stone-50/50">
							<Input
								name="value"
								placeholder="0.00"
								type="number"
								step="0.01"
							/>
						</InputWrapper>
					</div>

					{/* Status do Plano */}
					<div className="space-y-2">
						<InputLabel
							text="STATUS DO PLANO"
							className="text-[10px] font-black uppercase tracking-widest text-stone-300"
						/>
						<InputWrapper>
							<Select
								value={active}
								onValueChange={(value) => setActive(value)}
							>
								<SelectTrigger className="flex-1 bg-transparent text-sm text-stone-900">
									<SelectValue>
										{active === 'true'
											? 'Ativo'
											: 'Inativo'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="true">Ativo</SelectItem>
									<SelectItem value="false">
										Inativo
									</SelectItem>
								</SelectContent>
							</Select>
						</InputWrapper>
					</div>

					{/* Status de Pagamento */}
					<div className="space-y-2">
						<InputLabel
							text="STATUS DE PAGAMENTO"
							className="text-[10px] font-black uppercase tracking-widest text-stone-300"
						/>
						<InputWrapper>
							<Select
								value={paymentStatus}
								onValueChange={(value) =>
									setPaymentStatus(value)
								}
							>
								<SelectTrigger className="flex-1 bg-transparent text-sm text-stone-900">
									<SelectValue>
										{paymentStatus === 'PENDING'
											? 'Pendente'
											: 'Pago'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="PENDING">
										Pendente
									</SelectItem>
									<SelectItem value="PAID">Pago</SelectItem>
								</SelectContent>
							</Select>
						</InputWrapper>
					</div>
				</div>

				{/* Ações */}
				<div className="flex flex-col gap-4 pt-4 md:flex-row">
					<button
						type="button"
						onClick={onBack}
						className="flex-1 rounded-xl border border-stone-200 py-3 text-sm font-bold text-stone-900 transition-all hover:bg-stone-50"
					>
						Cancelar
					</button>
					<Button
						text={isSubmitting ? 'Criando...' : 'Criar Empresa'}
						type="submit"
						disabled={isSubmitting}
						className="flex-1 rounded-xl bg-black py-3 text-sm font-bold text-white shadow-lg transition-all hover:bg-stone-800"
					/>
				</div>
			</form>
		</div>
	);
};
